import * as React from 'react';
import { 
    Typography, 
    Box, 
    Container, 
    Button, 
    Card, 
    Grid, 
    CardContent, 
    Paper
} from '@mui/material';
import { connect } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import { main_button } from '../../app/components/Styles';
import { useEffect } from 'react';
import { getOrders, getProducts, getStore, getWarehouses, getDashboard } from '../../app/store/actions/imsWarehouseStaffActions';
import { 
    Inventory, 
    LocalShipping, 
    People, 
    Receipt, 
    Warehouse, 
    Warning,
    Assessment,
    ShoppingCart,
    CheckCircle,
    TrendingUp,
    Store
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { BrowserView } from 'react-device-detect';
import OrdersTable from '../../app/components/OrdersTable';

export const IMSWarehouseStaffHome = ({ getOrders, orders, getProducts, products, getDashboard, dashboard, user }) => {
    const navigate = useNavigate();
    const eventId = useParams().id;

    useEffect(() => {
        getDashboard();
        getOrders({ page: 1, limit: 10, inside: true });
        getProducts({ page: 1, limit: 10, hasInbounds: true });
    }, [getDashboard, getOrders, getProducts]);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header with Navigation Buttons */}
            <Paper 
                elevation={2}
                sx={{ 
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    backgroundColor: 'white'
                }}
            >
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: 1.5,
                            backgroundColor: 'var(--secColor)',
                            color: 'white'
                        }}
                    >
                        <Assessment color="white" sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography variant='h4' sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                        {user?.store?.name || 'Warehouse Dashboard'}
                    </Typography>
                </Box>

                <BrowserView>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/warehouse-staff/action-center")}
                                startIcon={<Warning />}
                            >
                                Action Center
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/warehouse-staff/packing")}
                                startIcon={<LocalShipping />}
                            >
                                Packing System
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/warehouse-staff/orders")}
                                startIcon={<Receipt />}
                            >
                                Orders
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/warehouse-staff/products")}
                                startIcon={<Inventory />}
                            >
                                Products
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/warehouse-staff/warehouses")}
                                startIcon={<Warehouse />}
                            >
                                Warehouses
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Button 
                                fullWidth 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate("/ims/warehouse-staff/employees")}
                                startIcon={<People />}
                            >
                                Employees
                            </Button>
                        </Grid>
                    </Grid>
                </BrowserView>
            </Paper>

            {/* Dashboard Statistics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Orders to Pack & Ship Combined Card */}
                <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                    Orders to Pack & Ship
                                </Typography>
                                <ShoppingCart sx={{ color: 'var(--secColor)', fontSize: 32 }} />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
                                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                                        To Pack
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: "#2196F3", fontWeight: 600 }}>
                                        {dashboard?.ordersLeftToPack || 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, textAlign: 'center', p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>
                                        To Ship
                                    </Typography>
                                    <Typography variant="h3" sx={{ color: "#FF9800", fontWeight: 600 }}>
                                        {dashboard?.ordersLeftToSend || 0}
                                    </Typography>
                                </Box>
                            </Box>
                            <Button 
                                fullWidth
                                variant="contained" 
                                sx={{ ...main_button, py: 1.5 }} 
                                onClick={() => navigate('/ims/warehouse-staff/packing')}
                            >
                                Go to Packing System
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Orders This Month Card */}
                <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    Total Orders This Month
                                </Typography>
                                <TrendingUp sx={{ color: 'var(--secColor)', fontSize: 32 }} />
                            </Box>
                            <Typography variant="h2" sx={{ color: "var(--secColor)", fontWeight: 600, mb: 1 }}>
                                {dashboard?.totalOrdersThisMonth || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Orders processed this month
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3}>
                {/* Success Rate Pie Chart */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    backgroundColor: 'var(--secColor)',
                                    color: 'white'
                                }}
                            >
                                <CheckCircle color="white" sx={{ fontSize: 24 }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                Order Success Rate
                            </Typography>
                        </Box>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Success', value: dashboard?.deliveredOrdersThisMonth || 0 },
                                        { name: 'Failure', value: dashboard?.rtoOrdersThisMonth || 0 },
                                    ]}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    label
                                >
                                    <Cell key="success" fill="#4CAF50" />
                                    <Cell key="failure" fill="#E45417" />
                                </Pie>
                                <Legend />
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography variant="h4" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                {dashboard?.successRate?.toFixed(2) || 0}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Success Rate
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Top Brands Bar Chart */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    backgroundColor: 'var(--secColor)',
                                    color: 'white'
                                }}
                            >
                                <Store color="white" sx={{ fontSize: 24 }} />
                            </Box>
                            <Typography variant="h6" sx={{ color: "var(--secColor)", fontWeight: 600 }}>
                                Top Brands
                            </Typography>
                        </Box>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={(dashboard?.topVendors || []).map(([name, value]) => ({ name, value }))}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="name" 
                                    angle={-45}
                                    textAnchor="end"
                                    height={100}
                                    interval={0}
                                />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" fill="var(--secColor)" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

const mapStateToProps = (state) => ({
    store: state?.records?.store,
    warehouses: state?.records?.warehouses,
    orders: state?.records?.orders,
    products: state?.records?.products,
    dashboard: state?.records?.dashboard,
    user: state?.auth?.user
});

const mapDispatchToProps = { getOrders, getProducts, getDashboard };

export default connect(mapStateToProps, mapDispatchToProps)(IMSWarehouseStaffHome);